(() => {
  const normalizeImageUrl = (value) => {
    if (!value) return '';
    const url = typeof value === 'string' ? value : value.src || value.url || '';
    return url.startsWith('//') ? `https:${url}` : url;
  };

  const normalizeText = (value) =>
    (value || '')
      .replace(/\s+/g, ' ')
      .replace(/(?:unavailable|sold out)$/i, '')
      .trim()
      .toLowerCase();

  const formatMoney = (cents, currency) => {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100);
    } catch (_) {
      return `${(cents / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  };

  const getSectionId = (root) => root.dataset.section || '';

  const getMainButton = (root) => {
    const sectionId = getSectionId(root);
    return root.querySelector(`#ProductSubmitButton-${CSS.escape(sectionId)}`);
  };

  const getProductForm = (root) => {
    const sectionId = getSectionId(root);
    return root.querySelector(`#product-form-${CSS.escape(sectionId)}`);
  };

  const getSelectedVariantId = (root) => getProductForm(root)?.querySelector("input[name='id']")?.value || '';

  const setVariantInputDirectly = (root, variantId) => {
    const input = getProductForm(root)?.querySelector("input[name='id']");
    if (!input) return;
    input.disabled = false;
    input.value = String(variantId);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const selectMainPickerVariant = (root, variant) => {
    const selectors = root.querySelector('variant-selects');
    if (!selectors || !variant?.options?.length) {
      setVariantInputDirectly(root, variant?.id);
      return false;
    }

    const groups = Array.from(selectors.querySelectorAll('.product-form__input'));
    let eventTarget = null;
    let matchedAll = true;

    variant.options.forEach((optionText, index) => {
      const group = groups[index];
      if (!group) {
        matchedAll = false;
        return;
      }

      const wanted = normalizeText(optionText);
      const select = group.querySelector('select');
      if (select) {
        const match = Array.from(select.options).find((option) => normalizeText(option.textContent) === wanted);
        if (!match) {
          matchedAll = false;
          return;
        }
        select.value = match.value;
        eventTarget = select;
        return;
      }

      const radios = Array.from(group.querySelectorAll("input[type='radio']"));
      const radio = radios.find((input) => {
        const label = group.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (!label) return false;
        const clone = label.cloneNode(true);
        clone.querySelectorAll('.visually-hidden, .product-form__swatch').forEach((node) => node.remove());
        return normalizeText(clone.textContent) === wanted;
      });

      if (!radio) {
        matchedAll = false;
        return;
      }

      radio.checked = true;
      eventTarget = radio;
    });

    setVariantInputDirectly(root, variant.id);

    if (!eventTarget || !matchedAll) return false;
    eventTarget.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  const initStickyAtc = async (root) => {
    if (!root || root.dataset.cpStickyAtcReady === 'true') return;

    const setting = root.querySelector('[data-cp-sticky-atc-enabled]');
    if (!setting || setting.dataset.cpStickyAtcEnabled !== 'true') return;

    const mainButton = getMainButton(root);
    if (!mainButton) return;

    root.dataset.cpStickyAtcReady = 'true';

    const sectionId = getSectionId(root);
    const currency = window.Shopify?.currency?.active || 'USD';
    let productData = null;

    try {
      const response = await fetch(`${root.dataset.url}.js`, { headers: { Accept: 'application/json' } });
      if (response.ok) productData = await response.json();
    } catch (_) {
      productData = null;
    }

    const selectedVariantJson = root.querySelector('variant-selects [data-selected-variant]');
    let fallbackVariant = null;
    try {
      fallbackVariant = selectedVariantJson ? JSON.parse(selectedVariantJson.textContent) : null;
    } catch (_) {
      fallbackVariant = null;
    }

    const variants = productData?.variants?.length ? productData.variants : fallbackVariant ? [fallbackVariant] : [];
    const selectedId = String(getSelectedVariantId(root));
    let currentVariant =
      variants.find((variant) => String(variant.id) === selectedId) ||
      variants.find((variant) => variant.available) ||
      variants[0] ||
      fallbackVariant;

    const sticky = document.createElement('div');
    sticky.className = `cp-sticky-atc${variants.length <= 1 ? ' cp-sticky-atc--single-variant' : ''}`;
    sticky.dataset.cpStickyAtc = '';
    sticky.setAttribute('role', 'region');
    sticky.setAttribute('aria-label', 'Quick add to cart');
    sticky.setAttribute('aria-hidden', 'true');

    const inner = document.createElement('div');
    inner.className = 'cp-sticky-atc__inner';

    const identity = document.createElement('div');
    identity.className = 'cp-sticky-atc__identity';

    const image = document.createElement('img');
    image.className = 'cp-sticky-atc__image';
    image.width = 56;
    image.height = 56;
    image.loading = 'lazy';
    image.alt = '';

    const identityCopy = document.createElement('div');
    identityCopy.className = 'cp-sticky-atc__identity-copy';
    const vendor = document.createElement('span');
    vendor.className = 'cp-sticky-atc__vendor';
    vendor.textContent = productData?.vendor || '';
    const title = document.createElement('strong');
    title.className = 'cp-sticky-atc__product-title';
    title.textContent =
      productData?.title ||
      root.querySelector('.product__title')?.textContent?.trim() ||
      root.querySelector('.cp-main-product__display-title')?.textContent?.trim() ||
      document.title;
    identityCopy.append(vendor, title);
    identity.append(image, identityCopy);

    const picker = document.createElement('div');
    picker.className = 'cp-sticky-atc__picker';
    const pickerTitle = document.createElement('strong');
    pickerTitle.className = 'cp-sticky-atc__variant-title';
    const pickerMeta = document.createElement('span');
    pickerMeta.className = 'cp-sticky-atc__variant-meta';
    const caret = document.createElement('span');
    caret.className = 'cp-sticky-atc__caret';
    caret.setAttribute('aria-hidden', 'true');
    caret.innerHTML = '<svg viewBox="0 0 20 20" fill="none"><path d="m5 7.5 5 5 5-5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    let stickySelect = null;
    if (variants.length > 1) {
      stickySelect = document.createElement('select');
      stickySelect.className = 'cp-sticky-atc__select';
      stickySelect.setAttribute('aria-label', 'Choose product variant');
      variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = `${variant.title}${variant.available ? '' : ' — Sold out'}`;
        option.disabled = !variant.available;
        if (currentVariant && String(variant.id) === String(currentVariant.id)) option.selected = true;
        stickySelect.appendChild(option);
      });
      picker.append(stickySelect);
    }
    picker.append(pickerTitle, pickerMeta, caret);

    const stickyButton = document.createElement('button');
    stickyButton.type = 'button';
    stickyButton.className = 'cp-sticky-atc__button button button--primary';
    const desktopButtonLabel = document.createElement('span');
    desktopButtonLabel.className = 'cp-sticky-atc__button-label cp-sticky-atc__button-label--desktop';
    const mobileButtonLabel = document.createElement('span');
    mobileButtonLabel.className = 'cp-sticky-atc__button-label cp-sticky-atc__button-label--mobile';
    stickyButton.append(desktopButtonLabel, mobileButtonLabel);

    inner.append(identity, picker, stickyButton);
    sticky.appendChild(inner);
    root.appendChild(sticky);

    const updateVariantUi = (variant) => {
      if (!variant) return;
      currentVariant = variant;

      const imageUrl = normalizeImageUrl(
        variant.featured_image || productData?.featured_image || productData?.images?.[0]
      );
      if (imageUrl) {
        image.src = imageUrl;
        image.hidden = false;
      } else {
        image.hidden = true;
      }

      pickerTitle.textContent =
        variant.title && variant.title !== 'Default Title' ? variant.title : productData?.title || title.textContent;
      pickerMeta.textContent = formatMoney(Number(variant.price), currency);
      if (stickySelect && stickySelect.value !== String(variant.id)) stickySelect.value = String(variant.id);
    };

    const updateButtonUi = () => {
      const mainText = mainButton.querySelector('span:not(.sold-out-message)')?.textContent?.trim() || 'Add to cart';
      const disabled =
        mainButton.disabled ||
        mainButton.getAttribute('aria-disabled') === 'true' ||
        currentVariant?.available === false;

      stickyButton.disabled = disabled;
      stickyButton.classList.toggle('is-disabled', disabled);
      desktopButtonLabel.textContent = mainText;
      mobileButtonLabel.textContent = disabled ? mainText : 'ADD';
    };

    updateVariantUi(currentVariant);
    updateButtonUi();

    stickySelect?.addEventListener('change', () => {
      const nextVariant = variants.find((variant) => String(variant.id) === stickySelect.value);
      if (!nextVariant) return;

      updateVariantUi(nextVariant);
      stickyButton.disabled = true;
      selectMainPickerVariant(root, nextVariant);

      window.setTimeout(() => {
        if (String(getSelectedVariantId(root)) !== String(nextVariant.id)) {
          setVariantInputDirectly(root, nextVariant.id);
        }
        updateButtonUi();
      }, 650);
    });

    stickyButton.addEventListener('click', () => {
      if (stickyButton.disabled) return;
      mainButton.click();
    });

    const mainButtonObserver = new MutationObserver(updateButtonUi);
    mainButtonObserver.observe(mainButton, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!data || String(data.sectionId) !== String(sectionId) || !data.variant) return;
        const matched = variants.find((variant) => String(variant.id) === String(data.variant.id)) || data.variant;
        updateVariantUi(matched);
        updateButtonUi();
      });
    }

    let ticking = false;
    const updateVisibility = () => {
      ticking = false;
      const rect = mainButton.getBoundingClientRect();
      const visible = rect.bottom < 0 && window.scrollY > 0;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };

    const requestVisibilityUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateVisibility);
    };

    window.addEventListener('scroll', requestVisibilityUpdate, { passive: true });
    window.addEventListener('resize', requestVisibilityUpdate, { passive: true });
    updateVisibility();
  };

  const init = (scope = document) => {
    scope.querySelectorAll?.("[id^='MainProduct-']").forEach((root) => initStickyAtc(root));
    if (scope.matches?.("[id^='MainProduct-']")) initStickyAtc(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
